import * as amplitude from "@amplitude/analytics-browser";
import { plugin as engagementPlugin } from "@amplitude/engagement-browser";

import AmplitudeEventNames from "./amplitudeEventNames";
import { logError } from "./logUtils";

let isAnalyticsSdkInitialized = false;

const MOBILE_CLIENTS = new Set(["mobile_ios", "mobile_android"]);

/**
 * Initializes Amplitude engagement tracking for a user.
 *
 * Uses the user's hashid as the Amplitude user_id so that browser-side engagement
 * events align with server-side product events (which also identify users by hashid
 * via CustomUser.external_user_id). Mismatched ids would split a user across two
 * Amplitude profiles and double-count them for MTU.
 *
 * Amplitude sets a third-party cookie (AMP_*) that is not strictly necessary, so this
 * function refuses to initialize unless the user has consented to all cookies. See BUG-625.
 *
 * @param {Object} user - The user object to initialize engagement tracking for
 * @param {string} user.hashid - The user's hashid (matches backend external_user_id)
 * @param {boolean} user.is_anonymous - Whether the user is anonymous
 * @param {string} user.cookie_consent_choice - The user's cookie consent choice ("all" required to init)
 * @param {string | null | undefined} apiKey - Public Amplitude browser key supplied at runtime
 * @returns {Promise<void>} A promise that resolves when initialization is complete
 */
export const initializeAmplitudeEngagement = async (user, apiKey) => {
    if (isAnalyticsSdkInitialized || !apiKey || !user?.hashid || user.is_anonymous || user.cookie_consent_choice !== "all") {
        return;
    }

    const userId = user.hashid;

    try {
        const options = {
            // We default everything to EU amplitude instance
            serverZone: "EU",
            defaultTracking: false,
        };

        amplitude.init(apiKey, userId, options);
        amplitude.add(engagementPlugin());
        isAnalyticsSdkInitialized = true;
    } catch (error) {
        logError(new Error(`Failed to initialize Amplitude engagement: ${error.message}`));
    }
};

/**
 * Emits a browser-side page view from the mobile apps so Guides and Surveys can
 * trigger on it.
 *
 * Amplitude only evaluates an "on event tracked" trigger against events the SDK
 * observes in the browser; the page_view we log from Django never reaches it. This
 * copy carries its own event name so the server-side page_view series stays clean.
 *
 * No-ops until initializeAmplitudeEngagement has run, which is what keeps this
 * behind the cookie-consent and feature-flag gates.
 *
 * @param {Object} params
 * @param {string} [params.client] - Originating surface (web, teams, desktop, mobile_ios, mobile_android)
 * @param {string} [params.path] - Path of the page being viewed
 */
export const trackPageView = ({ client, path } = {}) => {
    if (!isAnalyticsSdkInitialized || !MOBILE_CLIENTS.has(client)) {
        return;
    }

    try {
        // Mirrors the server-emitted page_view's property shape — client nested under
        // extra_data, path alongside it — so one property vocabulary covers both.
        amplitude.track(AmplitudeEventNames.MOBILE_CLIENTSIDE_PAGE_VIEWED, { extra_data: { client }, path });
    } catch (error) {
        logError(new Error(`Failed to track Amplitude page view: ${error.message}`));
    }
};
